/*
 * Zayian Designs — Custom Brass Gallery Rail Builder (UI controller)
 * Loaded as an ES module only on pages that contain the rail builder section.
 * Shares the look of the console builder (zd-console-builder.css) and its Three.js build.
 * Three.js and the 3D viewer are imported on demand; the page stays usable without WebGL.
 */
import * as core from './zd-rail-core.js?v=2';
import { PRICING } from './zd-rail-pricing.js?v=2';

const STORAGE_KEY = 'zd-rail-builder:v1';
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const safeStorage = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  del(k) { try { localStorage.removeItem(k); } catch (e) { /* ignore */ } }
};
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const IN = (n) => n * core.MM_PER_IN;

const ICON = {
  rotate: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5"/></svg>',
  dims: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 8h18M3 5v6M21 5v6M12 21V13M9 14l3-3 3 3"/></svg>',
  full: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  png: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 20h14"/></svg>',
  pin: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h14M5 15h14M9 5v14"/></svg>',
  minus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12"/></svg>',
  plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12M12 6v12"/></svg>',
  check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>'
};
// Placeholder while the 3D thumbnails render (replaced by real 3D renders of each module).
const PLACEHOLDER = '<span class="zd-thumb__ph" aria-hidden="true"></span>';

export class RailBuilder {
  constructor(root) {
    this.root = root;
    const cfgEl = root.querySelector('script[data-zd-config]');
    let override = {};
    try { override = JSON.parse(cfgEl?.textContent || '{}'); } catch (e) { console.error('[zd-rail] invalid config JSON', e); }
    if (typeof override.presetsText === 'string') {
      const parsed = override.presetsText.split(/\r?\n/).map((l) => l.split('|').map((x) => x.trim())).filter((a) => a.length >= 3 && a[0])
        .map(([label, L, H]) => ({ id: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'), label, L: Number(L), H: Number(H) }))
        .filter((p) => p.L > 0 && p.H > 0);
      if (parsed.length) override.presets = parsed;
      delete override.presetsText;
    }
    if (Array.isArray(override.finishes)) override.finishes = override.finishes.filter((f) => f && f.id && f.availability !== 'hidden');
    this.config = core.mergeConfig(core.DEFAULT_CONFIG, override);
    const titleCents = (t) => { const m = /([\d,]+(?:\.\d{1,2})?)/.exec(String(t || '')); return m ? Math.round(parseFloat(m[1].replace(/,/g, '')) * 100) : null; };
    this.config.pricing.ladder = (this.config.pricing.ladder || []).map((v) => ({ ...v, shopCents: titleCents(v.title) })).filter((v) => v.shopCents && v.available !== false);
    this.currency = { shop: this.config.pricing.currency || 'USD', active: (window.Shopify && Shopify.currency && Shopify.currency.active) || this.config.pricing.currency || 'USD', rate: Number((window.Shopify && Shopify.currency && Shopify.currency.rate) || 1) };
    this.sid = root.dataset.sectionId || 'zd';
    this.raw = {};
    this.viewer = null;
    this.viewerMode = 'loading';
    this.busy = false;
    this.qty = 1;
    this.thumbs = {};
    this.restore();
    this.render();
    this.bind();
    this.update({ initial: true });
    this.initViewer();
  }

  /* ============================================================ state */
  restore() {
    const shared = new URLSearchParams(location.search).get('design');
    this.notice = null;
    if (shared) {
      const r = core.decodeShare(shared, this.config);
      if (r) { this.state = r.state; this.notice = { text: r.changed ? 'Loaded a shared design. Some options were no longer available and were reset.' : 'Loaded a shared design.', kind: 'info' }; return; }
      this.notice = { text: 'That design link could not be read, so we started a new design.', kind: 'warn' };
    }
    const saved = safeStorage.get(STORAGE_KEY);
    if (saved) {
      try { this.state = core.sanitizeState(JSON.parse(saved), this.config).state; if (!this.notice) this.notice = { text: 'We restored your last design.', kind: 'info', reset: true }; return; } catch (e) { /* ignore */ }
    }
    this.state = core.defaultState(this.config);
  }
  save = debounce(() => safeStorage.set(STORAGE_KEY, JSON.stringify(core.compactState(this.state))), 250);

  /* ============================================================ render (once) */
  render() {
    const C = this.config;
    const id = (x) => `zd-${this.sid}-${x}`;
    this.ids = id;
    const card = (name, value, title, body, opts = {}) => `
      <label class="zd-card${opts.cls ? ' ' + opts.cls : ''}">
        <input type="radio" name="${id(name)}" value="${esc(value)}" data-field="${name}">
        <span class="zd-card__inner">
          ${opts.art === false ? '' : `<span class="zd-card__art zd-thumb" data-thumb="${esc(opts.thumb || name + ':' + value)}">${opts.art || PLACEHOLDER}</span>`}
          <span class="zd-card__text"><span class="zd-card__title">${esc(title)}</span><span class="zd-card__price" data-delta="${name}:${esc(value)}"></span>${body ? `<span class="zd-card__body">${esc(body)}</span>` : ''}${opts.meta ? `<span class="zd-card__meta">${esc(opts.meta)}</span>` : ''}</span>
          <span class="zd-card__check" aria-hidden="true">${ICON.check}</span>
        </span>
      </label>`;
    const seg = (name, items, label) => `
      <fieldset class="zd-fieldset zd-segmented zd-segmented--wrap"><legend class="zd-label">${label}</legend>
        ${items.map(([v, t]) => `<label><input type="radio" name="${id(name)}" value="${esc(v)}" data-field="${name}"><span>${esc(t)}</span></label>`).join('')}
      </fieldset>`;
    const step = (n, key, title, intro, body) => `
      <section class="zd-step" id="${id('step-' + key)}" aria-labelledby="${id('step-' + key + '-h')}">
        <header class="zd-step__head">
          <span class="zd-step__num" aria-hidden="true">${n}</span>
          <div><h2 class="zd-step__title" id="${id('step-' + key + '-h')}"><span class="zd-visually-hidden">Step ${n}: </span>${title}</h2>
          <p class="zd-step__intro">${intro}</p></div>
          <span class="zd-step__value" data-summary="${key}" aria-hidden="true"></span>
        </header>
        <div class="zd-step__body">${body}</div>
      </section>`;
    const dimField = (k, label) => `
      <div class="zd-field" data-dim="${k}">
        <label class="zd-field__label" for="${id('dim-' + k)}">${label} <span class="zd-field__unit" data-unit-label></span></label>
        <div class="zd-stepper">
          <button type="button" class="zd-stepper__btn" data-step="${k}" data-dir="-1" aria-label="Decrease ${label.toLowerCase()}">${ICON.minus}</button>
          <input class="zd-input" id="${id('dim-' + k)}" type="text" inputmode="decimal" autocomplete="off" spellcheck="false" data-dim-input="${k}" aria-describedby="${id('dim-' + k + '-hint')} ${id('dim-' + k + '-err')}">
          <button type="button" class="zd-stepper__btn" data-step="${k}" data-dir="1" aria-label="Increase ${label.toLowerCase()}">${ICON.plus}</button>
        </div>
        <p class="zd-field__hint" id="${id('dim-' + k + '-hint')}" data-hint="${k}"></p>
        <p class="zd-field__error" id="${id('dim-' + k + '-err')}" data-error="${k}" role="alert" hidden></p>
      </div>`;
    const finishes = core.enabledFinishes(C);

    const steps = [
      step(1, 'post', 'Post design', 'Every Kinycraft post, modelled in 3D. Pick the one that suits your shelves.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Post design</legend>
          <div class="zd-cards zd-cards--3 zd-cards--posts">${core.POSTS.map((p) => card('post', p.id, p.name, p.text, { meta: `As on our ${p.ref}` })).join('')}</div>
          <p class="zd-field__error" data-error="post" role="alert" hidden></p>
        </fieldset>`),
      step(2, 'size', 'Layout &amp; size', 'Choose where the rail runs, then enter its size. Decimals welcome.', `
        <fieldset class="zd-fieldset"><legend class="zd-label">Layout</legend>
          <div class="zd-cards zd-cards--2 zd-cards--compact">${core.LAYOUTS.map((l) => card('layout', l.id, l.name, l.text)).join('')}</div>
        </fieldset>
        <div class="zd-row zd-row--between">
          <fieldset class="zd-fieldset zd-segmented"><legend class="zd-visually-hidden">Measurement units</legend>
            <label><input type="radio" name="${id('units')}" value="in" data-field="units"><span>Inches</span></label>
            <label><input type="radio" name="${id('units')}" value="cm" data-field="units"><span>Centimetres</span></label>
          </fieldset>
          <span class="zd-muted zd-small">Values convert exactly when you switch.</span>
        </div>
        <fieldset class="zd-fieldset"><legend class="zd-label">Quick sizes</legend>
          <div class="zd-presets">
            ${C.presets.map((p) => `<label class="zd-preset"><input type="radio" name="${id('preset')}" value="${esc(p.id)}" data-field="preset"><span><strong>${esc(p.label)}</strong><span class="zd-preset__dims" data-preset-dims="${esc(p.id)}"></span></span></label>`).join('')}
            <label class="zd-preset"><input type="radio" name="${id('preset')}" value="custom" data-field="preset"><span><strong>Custom</strong><span class="zd-preset__dims">Your own size</span></span></label>
          </div>
        </fieldset>
        <div class="zd-dims">
          <div data-length-wrap>${dimField('L', 'Rail length')}</div>
          <div data-return-wrap>${dimField('R', 'Return depth')}</div>
        </div>
        <p class="zd-small zd-muted" data-length-note>Rail length is the overall size, end to end. Choose 1–2″ shorter than your shelf for a neat finish. Rails stand 3″ high.</p>`),
      step(3, 'details', 'Rails &amp; details', 'Number of rods, rod size, post spacing and rail ends.', `
        <fieldset class="zd-fieldset"><legend class="zd-label">Number of rails</legend>
          <div class="zd-cards zd-cards--3 zd-cards--compact">
            ${card('rails', '1', 'Single rail', 'One rod at the top.')}
            ${card('rails', '2', 'Double rail', 'A second rod at mid height.')}
            ${card('rails', '3', 'Triple rail', 'Three rods — a true gallery.')}
          </div>
          <p class="zd-field__error" data-error="rails" role="alert" hidden></p>
        </fieldset>
        ${seg('rod', C.rods.map((r) => [r.id, r.name]), 'Rod size')}
        <fieldset class="zd-fieldset"><legend class="zd-label">Post spacing</legend>
          <div class="zd-cards zd-cards--3 zd-cards--compact">
            ${card('spacing', 'standard', 'Standard', 'Posts up to 24″ apart, as in our catalogue.')}
            ${card('spacing', 'close', 'Close', 'Posts up to 16″ apart for heavy plates.')}
            ${card('spacing', 'gallery', 'Gallery', 'Posts up to 10″ apart — a balustrade look.')}
          </div>
          <p class="zd-small zd-muted" data-post-count></p>
        </fieldset>
        <fieldset class="zd-fieldset" data-ends-wrap><legend class="zd-label">Rail ends</legend>
          <div class="zd-cards zd-cards--3 zd-cards--compact">
            ${card('ends', 'flush', 'Flush at end post', 'The rod finishes inside the end post.')}
            ${card('ends', 'ball', 'Ball end caps', 'A solid brass ball just past each end post.')}
            ${card('ends', 'finial', 'Turned finials', 'A small turned finial at each end.')}
          </div>
        </fieldset>`),
      step(4, 'shelf', 'Shelf', 'Add a made-to-measure shelf, or fit the rail to a shelf you already have.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Shelf</legend>
          <div class="zd-cards zd-cards--3 zd-cards--compact">${core.SHELVES.map((s) => card('shelfType', s.id, s.name, s.text)).join('')}</div>
        </fieldset>
        <fieldset class="zd-fieldset" data-wood-wrap><legend class="zd-label">Wood</legend>
          <div class="zd-cards zd-cards--finish">${core.WOODS.map((w) => card('wood', w.id, w.name, '', { thumb: 'wood:' + w.id })).join('')}</div>
        </fieldset>
        <p class="zd-label" data-shelf-size-label>Shelf size</p>
        <label class="zd-check" data-auto-wrap><input type="checkbox" data-shelf-auto> <span>Make the shelf 2″ longer than the rail</span></label>
        <div class="zd-dims">
          <div data-sl-wrap>${dimField('SL', 'Shelf length')}</div>
          ${dimField('SD', 'Shelf depth')}
        </div>
        ${seg('thickness', core.THICKNESSES.map((t) => [String(t), `${t}″`]), 'Shelf thickness')}
        <p class="zd-small zd-muted" data-shelf-note></p>`),
      step(5, 'brackets', 'Shelf brackets', 'Optional solid brass brackets, sized to the shelf depth.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Brackets</legend>
          <div class="zd-cards zd-cards--3 zd-cards--compact">${core.BRACKETS.map((b) => card('bracket', b.id, b.name, b.text)).join('')}</div>
        </fieldset>
        <div class="zd-field" data-bcount-wrap>
          <span class="zd-field__label" id="${id('bc-l')}">Number of brackets</span>
          <div class="zd-row">
            <div class="zd-stepper zd-stepper--sm" role="group" aria-labelledby="${id('bc-l')}">
              <button type="button" class="zd-stepper__btn" data-action="bc-" aria-label="Fewer brackets">${ICON.minus}</button>
              <output class="zd-qty__val" data-bcount aria-live="polite"></output>
              <button type="button" class="zd-stepper__btn" data-action="bc+" aria-label="More brackets">${ICON.plus}</button>
            </div>
            <button type="button" class="zd-link" data-action="bc-auto">Use recommended</button>
          </div>
          <p class="zd-field__hint" data-bcount-hint></p>
          <p class="zd-field__error" data-error="brackets" role="alert" hidden></p>
        </div>`),
      step(6, 'finish', 'Finish', 'One finish for the rail, brackets and hammered brass. Colours on screen are approximate.', `
        <fieldset class="zd-fieldset"><legend class="zd-visually-hidden">Finish</legend>
          <div class="zd-cards zd-cards--finish">${finishes.map((f) => card('finish', f.id, f.name, '', { art: `<span class="zd-swatch__chip zd-swatch__chip--lg" style="--zd-swatch:${esc(f.color)}" aria-hidden="true"></span>` })).join('')}</div>
          <p class="zd-field__error" data-error="finish" role="alert" hidden></p>
        </fieldset>
        <div class="zd-finish-detail" data-finish-detail aria-live="polite"></div>`),
      step(7, 'review', 'Review your design', 'Check every detail. Use Edit to change any selection.', `
        <dl class="zd-review" data-review></dl>
        <div class="zd-review-cols">
          <div><h3 class="zd-h3">Included</h3><ul class="zd-list zd-list--check" data-included></ul></div>
          <div><h3 class="zd-h3">Not included</h3><ul class="zd-list zd-list--x" data-excluded></ul></div>
        </div>
        <div class="zd-pricebox" data-pricebox></div>
        <ul class="zd-notes" data-warnings></ul>
        <div class="zd-tools">
          <button type="button" class="zd-btn zd-btn--ghost" data-action="share">Copy share link</button>
          <button type="button" class="zd-btn zd-btn--ghost" data-action="print">Print summary</button>
          <button type="button" class="zd-btn zd-btn--ghost" data-action="png">Download image</button>
          <button type="button" class="zd-btn zd-btn--ghost zd-btn--danger" data-action="reset">Reset design</button>
        </div>
        <div class="zd-share" data-share hidden>
          <label class="zd-field__label" for="${id('share-url')}">Share link <span class="zd-muted">(contains design options only — no personal details)</span></label>
          <div class="zd-row"><input class="zd-input" id="${id('share-url')}" readonly data-share-url><button type="button" class="zd-btn zd-btn--small" data-action="copy">Copy</button></div>
        </div>`)
    ];
    this.root.querySelector('[data-zd-steps]').innerHTML = steps.join('');

    const noticeEl = this.root.querySelector('[data-zd-notice]');
    if (noticeEl && this.notice) {
      noticeEl.hidden = false;
      noticeEl.className = `zd-notice zd-notice--${this.notice.kind}`;
      noticeEl.innerHTML = `<span>${esc(this.notice.text)}</span>${this.notice.reset ? '<button type="button" class="zd-link" data-action="reset">Start a new design</button>' : ''}<button type="button" class="zd-notice__close" data-action="dismiss-notice" aria-label="Dismiss">×</button>`;
    }
    this.root.querySelector('[data-zd-toolbar]').innerHTML = `
      <div class="zd-toolbar__group" role="group" aria-label="Camera views">
        ${['perspective', 'front', 'side', 'top', 'detail'].map((v) => `<button type="button" class="zd-tool" data-view="${v}" aria-pressed="${v === 'perspective'}">${v === 'detail' ? 'Close-up' : v[0].toUpperCase() + v.slice(1)}</button>`).join('')}
      </div>
      <div class="zd-toolbar__group" role="group" aria-label="Viewer tools">
        <button type="button" class="zd-tool zd-tool--icon" data-action="reset-view" title="Reset view" aria-label="Reset view">${ICON.rotate}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="dims" aria-pressed="true" title="Show dimensions" aria-label="Show dimensions">${ICON.dims}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="fullscreen" aria-pressed="false" title="Full screen" aria-label="Full screen preview">${ICON.full}</button>
        <button type="button" class="zd-tool zd-tool--icon" data-action="png" title="Download PNG" aria-label="Download PNG of the current design">${ICON.png}</button>
        <button type="button" class="zd-tool zd-tool--icon zd-tool--mobile" data-action="pin" aria-pressed="false" title="Keep a compact preview visible" aria-label="Keep a compact preview visible while scrolling">${ICON.pin}</button>
      </div>`;
  }

  /* ============================================================ bind */
  bind() {
    const r = this.root;
    r.addEventListener('change', (e) => {
      const t = e.target;
      if (t.hasAttribute('data-shelf-auto')) {
        const sh = { ...this.state.shelf, auto: t.checked };
        if (!t.checked) sh.L = core.shelfLengthMm(this.state);
        delete this.raw.SL;
        return this.set({ shelf: sh }, { announce: t.checked ? 'Shelf length follows the rail' : 'Shelf length unlocked' });
      }
      const f = t.dataset.field;
      if (!f || t.type !== 'radio') return;
      const v = t.value;
      const s = this.state;
      switch (f) {
        case 'units': return this.setUnits(v);
        case 'preset': return this.applyPreset(v);
        case 'rails': return this.set({ rails: Number(v) }, { announce: `${v} rail${v === '1' ? '' : 's'}` });
        case 'shelfType': return this.set({ shelf: { ...s.shelf, type: v } }, { announce: core.SHELVES.find((x) => x.id === v)?.name });
        case 'wood': return this.set({ shelf: { ...s.shelf, wood: v } }, { announce: 'Wood updated' });
        case 'thickness': return this.set({ shelf: { ...s.shelf, T: Number(v) } }, { announce: `Shelf thickness ${v} inches` });
        case 'bracket': return this.set({ brackets: { type: v, count: 0 } }, { announce: core.BRACKETS.find((x) => x.id === v)?.name });
        case 'mount': {
          const patch = { mount: v };
          if (v === 'face' && s.shelf.auto) patch.shelf = { ...s.shelf, auto: false, L: s.dims.L };
          return this.set(patch, { announce: v === 'face' ? 'Face-mounted posts' : 'Top-mounted posts' });
        }
        default: return this.set({ [f]: v }, { announce: `${f} updated` });
      }
    });
    r.addEventListener('input', (e) => { const t = e.target; if (t.dataset.dimInput) this.onDimInput(t.dataset.dimInput, t.value); });
    r.addEventListener('blur', (e) => {
      const t = e.target;
      if (t.dataset && t.dataset.dimInput && this.raw[t.dataset.dimInput] !== undefined) {
        const k = t.dataset.dimInput;
        const p = core.parseLengthInput(this.raw[k], this.state.units);
        if (p.ok && Math.abs(p.mm - core.dimValue(this.state, k)) < 1e-9) { delete this.raw[k]; t.value = String(core.displayValue(core.dimValue(this.state, k), this.state.units)); }
      }
    }, true);
    r.addEventListener('click', (e) => {
      const b = e.target.closest('button');
      if (!b || !r.contains(b)) return;
      if (b.dataset.step) return this.stepValue(b.dataset.step, Number(b.dataset.dir));
      if (b.dataset.view) return this.setView(b.dataset.view);
      if (b.dataset.edit) return this.goToStep(b.dataset.edit);
      if (b.dataset.action) this.action(b.dataset.action, b);
    });
    r.addEventListener('focusin', (e) => { if (e.target.matches('input[type=text]')) r.classList.add('zd--typing'); });
    r.addEventListener('focusout', () => setTimeout(() => { if (!r.contains(document.activeElement) || !document.activeElement.matches('input[type=text]')) r.classList.remove('zd--typing'); }, 50));
    document.addEventListener('fullscreenchange', () => this.onFullscreenChange());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && r.classList.contains('zd--fs-fallback')) this.toggleFullscreen(false); });
    window.addEventListener('afterprint', () => document.documentElement.classList.remove('zd-is-printing'));
    document.addEventListener('shopify:section:unload', (e) => { if (e.detail && e.detail.sectionId === this.sid) this.destroy(); });
    const inline = r.querySelector('[data-zd-action]');
    if ('IntersectionObserver' in window && inline) {
      new IntersectionObserver((en) => en.forEach((x) => r.classList.toggle('zd--action-visible', x.isIntersecting)), { threshold: 0.2 }).observe(inline);
    }
  }

  set(patch, { announce } = {}) { Object.assign(this.state, patch); this.update({ announce }); }
  setUnits(u) {
    this.raw = Object.fromEntries(Object.entries(this.raw).filter(([, v]) => !core.parseLengthInput(v, this.state.units).ok));
    this.state.units = u;
    this.update({ announce: `Units set to ${u === 'cm' ? 'centimetres' : 'inches'}` });
  }
  applyPreset(pid) {
    if (pid === 'custom') return this.set({ preset: 'custom' });
    const p = this.config.presets.find((x) => x.id === pid);
    if (!p) return;
    ['L', 'H', 'SL'].forEach((k) => delete this.raw[k]);
    const shelf = { ...this.state.shelf };
    if (!shelf.auto) shelf.L = Math.max(shelf.L, IN(p.L + 2));
    this.set({ preset: pid, dims: { ...this.state.dims, L: IN(p.L), H: IN(p.H) }, shelf }, { announce: `${p.label} applied` });
  }
  writeDim(k, mm) {
    if (k === 'SL') this.state.shelf = { ...this.state.shelf, L: mm };
    else if (k === 'SD') this.state.shelf = { ...this.state.shelf, D: mm };
    else this.state.dims = { ...this.state.dims, [k]: mm };
    if (k === 'L' || k === 'H') this.state.preset = this.matchPreset();
  }
  onDimInput(k, value) {
    this.raw[k] = value;
    const p = core.parseLengthInput(value, this.state.units);
    if (p.ok && !core.checkDimension(k, p.mm, this.config, this.state.units).error) this.writeDim(k, p.mm);
    this.update({ skipDimInput: k });
  }
  stepValue(k, dir) {
    const u = this.state.units;
    const inc = u === 'cm' ? 1 : (k === 'H' || k === 'R' ? 0.25 : k === 'SD' ? 0.5 : 1);
    const raw = this.raw[k];
    const base = raw !== undefined && core.parseLengthInput(raw, u).ok ? Number(String(raw).replace(',', '.')) : core.displayValue(core.dimValue(this.state, k), u);
    const next = Math.max(inc, Math.round((base + dir * inc) / inc) * inc);
    delete this.raw[k];
    const mm = core.toMm(next, u);
    const lim = this.config.limits[{ L: 'length', R: 'ret', H: 'height', SL: 'shelfL', SD: 'shelfD' }[k]];
    this.writeDim(k, Math.min(IN(lim.hardMax), Math.max(IN(lim.hardMin), mm)));
    this.update({ announce: true });
  }
  matchPreset() {
    const d = this.state.dims;
    const p = this.config.presets.find((x) => Math.abs(IN(x.L) - d.L) < 0.05);
    return p ? p.id : 'custom';
  }

  /* ============================================================ update (every change) */
  update(opts = {}) {
    const C = this.config, s = this.state, r = this.root, u = s.units;
    // Fixed specification: 3″ rail height, posts screwed down on top of the shelf.
    s.mount = 'top';
    s.dims = { ...s.dims, H: IN(3) };
    this.validation = core.validateState(s, C, this.raw);
    this.pricing = core.priceState(s, C, this.validation);
    const v = this.validation;
    const L = core.labelsFor(s, C);
    const cur = { units: s.units, preset: s.preset, post: s.post, layout: s.layout, rails: String(s.rails), rod: s.rod, spacing: s.spacing, ends: s.ends, mount: s.mount,
      shelfType: s.shelf.type, wood: s.shelf.wood, thickness: String(s.shelf.T), bracket: s.brackets.type, finish: s.finish };
    r.querySelectorAll('input[type=radio][data-field]').forEach((inp) => (inp.checked = String(cur[inp.dataset.field]) === inp.value));
    r.querySelectorAll('[data-unit-label]').forEach((el) => (el.textContent = u === 'cm' ? '(cm)' : '(in)'));
    C.presets.forEach((p) => { const el = r.querySelector(`[data-preset-dims="${CSS.escape(p.id)}"]`); if (el) el.textContent = `${core.formatLength(IN(p.L), u)} long`; });

    const hasShelf = s.shelf.type !== 'none';
    const face = s.mount === 'face';
    // visibility
    r.querySelector('[data-return-wrap]').hidden = s.layout === 'front';
    r.querySelector('[data-length-wrap]').hidden = face;
    r.querySelector('[data-ends-wrap]').hidden = s.layout === 'three';
    r.querySelector('[data-wood-wrap]').hidden = s.shelf.type !== 'wood';
    r.querySelector('[data-auto-wrap]').hidden = face;
    r.querySelector('[data-sl-wrap]').hidden = s.shelf.auto && !face;
    r.querySelector('[data-shelf-auto]').checked = !!s.shelf.auto && !face;
    r.querySelector('[data-shelf-size-label]').textContent = hasShelf ? 'Shelf size' : 'Your shelf size (for fit and the preview)';
    r.querySelector('[data-length-note]').textContent = face
      ? `Face-mounted posts wrap the shelf edge, so the rail length follows the shelf length (${L.length} overall). Set the shelf length in step 4.`
      : 'Rail length is the overall size, end to end. Choose 1–2″ shorter than your shelf for a neat finish. Rails stand 3″ high.';
    r.querySelector('[data-shelf-note]').textContent = s.shelf.type === 'hammered'
      ? 'Hand-hammered solid brass sheet over a hardwood core. The brass takes the finish you choose in step 6.'
      : s.shelf.type === 'wood' ? 'Solid hardwood with a hand-rubbed oil finish and hidden fixing points for the rail posts.' : 'Not sold here — we use these sizes to check the rail fits and to draw the preview.';
    // dimension inputs
    const limKey = { L: 'length', R: 'ret', H: 'height', SL: 'shelfL', SD: 'shelfD' };
    ['L', 'R', 'SL', 'SD'].forEach((k) => {
      const inp = r.querySelector(`[data-dim-input="${k}"]`);
      const val = k === 'SL' ? core.shelfLengthMm(s) : core.dimValue(s, k);
      if (opts.skipDimInput !== k) inp.value = this.raw[k] !== undefined ? this.raw[k] : String(core.displayValue(val, u));
      const lim = C.limits[limKey[k]];
      r.querySelector(`[data-hint="${k}"]`).textContent = `${core.formatLength(IN(lim.hardMin), u)} – ${core.formatLength(IN(lim.hardMax), u)}.`;
      this.showError(k, v.errors[k], inp);
    });
    ['post', 'finish', 'T', 'brackets', 'rails'].forEach((k) => this.showError(k, v.errors[k]));
    // posts & brackets
    const pc = this.pricing.posts || null;
    const pcEl = r.querySelector('[data-post-count]');
    if (pcEl) pcEl.textContent = pc ? `Your rail has ${pc.total} posts${pc.returns ? ` (${pc.front} on the front, ${pc.perReturn} on each return)` : ''}.` : '';
    const bw = r.querySelector('[data-bcount-wrap]');
    bw.hidden = s.brackets.type === 'none';
    const bc = core.bracketCount(s);
    r.querySelector('[data-bcount]').textContent = bc || '';
    r.querySelector('[data-bcount-hint]').textContent = s.brackets.count ? `Recommended for this shelf: ${core.bracketCount({ ...s, brackets: { ...s.brackets, count: 0 } })}.` : 'Recommended number for this shelf length.';

    const sum = {
      post: L.post,
      size: `${L.length}${s.layout !== 'front' ? ` + ${s.layout === 'three' ? '2 × ' : ''}${core.formatLength(s.dims.R, u)}` : ''} · ${L.height} high`,
      details: [L.rails, s.spacing !== 'standard' ? (s.spacing === 'close' ? 'close' : 'gallery') : '', face ? 'face-mount' : ''].filter(Boolean).join(', '),
      shelf: s.shelf.type === 'none' ? 'Rail only' : s.shelf.type === 'wood' ? core.WOODS.find((w) => w.id === s.shelf.wood)?.name : 'Hammered brass',
      brackets: s.brackets.type === 'none' ? 'None' : `${bc} × ${core.BRACKETS.find((b) => b.id === s.brackets.type)?.name.replace(' bracket', '')}`,
      finish: L.finish,
      review: core.designRef(s)
    };
    r.querySelectorAll('[data-summary]').forEach((el) => (el.textContent = sum[el.dataset.summary] || ''));
    this.sum = sum;
    this.renderDeltas();
    this.renderFinishDetail();
    this.renderReview(L);
    this.renderAction(L, sum);
    this.renderIncluded();
    this.updatePreview();
    this.queueThumbs();
    this.save();
    if (opts.announce && !opts.initial) this.announce(`${typeof opts.announce === 'string' ? opts.announce + '. ' : ''}${sum.size}. ${this.totalText()}`);
  }

  showError(key, msg, input) {
    const el = this.root.querySelector(`[data-error="${key}"]`);
    if (!el) return;
    el.hidden = !msg;
    el.textContent = msg || '';
    if (input) input.setAttribute('aria-invalid', msg ? 'true' : 'false');
    const field = el.closest('.zd-field');
    if (field) field.classList.toggle('is-invalid', !!msg);
  }
  renderFinishDetail() {
    const f = this.config.finishes.find((x) => x.id === this.state.finish);
    const el = this.root.querySelector('[data-finish-detail]');
    if (!f) { el.innerHTML = ''; return; }
    el.innerHTML = `<div class="zd-finish-detail__swatch" style="--zd-swatch:${esc(f.color)}" aria-hidden="true"></div>
      <div class="zd-finish-detail__text"><h3 class="zd-h3">${esc(f.name)}</h3><p>${esc(f.description)}</p>
      ${f.patina ? `<p><strong>Over time:</strong> ${esc(f.patina)}</p>` : ''}${f.care ? `<p><strong>Care:</strong> ${esc(f.care)}</p>` : ''}</div>`;
  }
  renderReview(L) {
    const rows = [['post', 'Post design', L.post], ['size', 'Layout', L.layout], ['size', 'Rail length', L.length], ['size', 'Returns', L.ret], ['size', 'Rail height', L.height],
      ['details', 'Rails', `${L.rails}, ${L.rod}`], ['details', 'Post spacing', L.spacing], ['details', 'Rail ends', L.ends],
      ['shelf', 'Shelf', L.shelf], ['brackets', 'Brackets', L.brackets], ['finish', 'Finish', L.finish]];
    this.root.querySelector('[data-review]').innerHTML = rows.map(([st, k, val]) => `
      <div class="zd-review__row"><dt>${k}</dt><dd>${esc(val)}</dd><dd class="zd-review__edit"><button type="button" class="zd-link" data-edit="${st}">Edit<span class="zd-visually-hidden"> ${k.toLowerCase()}</span></button></dd></div>`).join('');
    this.root.querySelector('[data-included]').innerHTML = core.includedComponents(this.state, this.config, this.pricing.posts).map((x) => `<li>${esc(x)}</li>`).join('');
    this.root.querySelector('[data-excluded]').innerHTML = core.excludedFor(this.state).map((x) => `<li>${esc(x)}</li>`).join('');
    this.root.querySelector('[data-pricebox]').innerHTML = `<h3 class="zd-h3">Price summary</h3>${this.breakdownHTML()}`;
    this.root.querySelector('[data-warnings]').innerHTML = this.validation.warnings.map((w) => `<li>${esc(w)}</li>`).join('');
  }

  /* ------------------------------------------------------------ money */
  money(c) { const { shop, active, rate } = this.currency; return active === shop ? core.formatMoney(c, this.config.pricing.moneyFormat) : this.moneyActive(Math.round(c * rate)); }
  moneyActive(c) {
    const { shop, active } = this.currency;
    if (active === shop) return core.formatMoney(c, this.config.pricing.moneyFormat);
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(c / 100); } catch (e) { return `${(c / 100).toFixed(2)} ${active}`; }
  }
  unitChargeCents() { return this.pricing.variant ? Number(this.pricing.variant.price) : null; }
  totalText() {
    const p = this.pricing;
    if (p.mode === 'direct') return this.moneyActive(this.unitChargeCents() * this.qty);
    if (p.mode === 'invalid') return 'Check highlighted fields';
    return p.unitCents == null ? 'Contact us for a quote' : 'Unavailable';
  }
  breakdownHTML() {
    const p = this.pricing;
    if (p.mode === 'invalid') return `<p class="zd-small">Correct the highlighted ${Object.keys(this.validation.errors).length > 1 ? 'fields' : 'field'} to see your price.</p>`;
    const L = core.labelsFor(this.state, this.config);
    const rows = p.lines.map((l) => `<tr><th scope="row">${l.key === 'base' ? `Gallery rail · ${esc(L.length)}<span class="zd-breakdown__sub">${esc(L.post)}, single ½″ rod, 3″ high, ${esc(L.finish.toLowerCase())}</span>` : esc(l.label)}</th><td>${l.key === 'base' ? '' : '+ '}${this.money(l.cents)}</td></tr>`).join('');
    const unit = this.unitChargeCents();
    return `<table class="zd-breakdown"><tbody>${rows}
      <tr class="zd-breakdown__unit"><th scope="row">Price per rail set</th><td>${unit != null ? this.moneyActive(unit) : '—'}</td></tr>
      <tr><th scope="row">Quantity</th><td>× ${this.qty}</td></tr>
      <tr class="zd-breakdown__total"><th scope="row">Total</th><td>${this.totalText()}</td></tr></tbody></table>
      <p class="zd-small zd-muted">This is the exact product price charged in your cart and at checkout. Shipping and any taxes are calculated at checkout.${this.currency.active !== this.currency.shop ? ' Line amounts are converted for display; the unit price and total are exact.' : ''}</p>`;
  }
  renderDeltas() {
    const s = this.state;
    const els = this.root.querySelectorAll('[data-delta]');
    if (!this.validation.valid) { els.forEach((el) => (el.textContent = '')); return; }
    const baseOf = { post: 'baluster', layout: 'front', rails: '1', spacing: 'standard', ends: 'flush', mount: 'top', shelfType: 'none', wood: 'oak', bracket: 'none', finish: (this.config.finishes[0] || {}).id };
    const withVal = (field, value) => {
      const t = structuredClone(s);
      if (field === 'rails') t.rails = Number(value);
      else if (field === 'shelfType') t.shelf.type = value;
      else if (field === 'wood') { t.shelf.type = 'wood'; t.shelf.wood = value; }
      else if (field === 'bracket') t.brackets = { type: value, count: 0 };
      else t[field] = value;
      return core.unitPrice(t);
    };
    const cache = {};
    els.forEach((el) => {
      const [field, value] = el.dataset.delta.split(':');
      if (!(field in baseOf)) return;
      const ref = cache[field] ?? (cache[field] = withVal(field, baseOf[field]));
      const d = (cache[field + value] ?? (cache[field + value] = withVal(field, value))) - ref;
      el.textContent = d > 0 ? `+ ${this.money(d)}` : 'Included';
      el.classList.toggle('is-extra', d > 0);
    });
  }
  renderAction(L, sum) {
    const p = this.pricing;
    const errCount = Object.keys(this.validation.errors).length;
    const summary = esc([L.post, sum.size, L.rails, L.finish].join(' · ')) + (this.state.shelf.type !== 'none' ? ' · ' + esc(sum.shelf) + ' shelf' : '') + (this.state.brackets.type !== 'none' ? ' · ' + esc(sum.brackets) : '');
    const addBtn = p.mode === 'direct'
      ? `<button type="button" class="zd-btn zd-btn--primary zd-btn--add" data-action="add-to-cart"><span class="zd-btn__label">Add to Cart — ${this.totalText()}</span><span class="zd-spinner" aria-hidden="true"></span></button>`
      : p.mode === 'invalid'
        ? `<button type="button" class="zd-btn zd-btn--primary" data-action="first-error">Fix ${errCount} highlighted ${errCount === 1 ? 'field' : 'fields'}</button>`
        : `<a class="zd-btn zd-btn--primary" href="/pages/contact">Contact us for a quote</a>`;
    const priceBlock = `<div class="zd-price"><span class="zd-price__label">Total${this.qty > 1 ? ` · ${this.qty} sets` : ''}</span><span class="zd-price__amount${p.mode === 'invalid' ? ' zd-price__amount--muted' : ''}">${this.totalText()}</span></div>`;
    const qty = `<div class="zd-qty"><span class="zd-field__label" id="${this.ids('qty-l')}">Quantity</span><div class="zd-stepper zd-stepper--sm" role="group" aria-labelledby="${this.ids('qty-l')}"><button type="button" class="zd-stepper__btn" data-action="qty-" aria-label="Decrease quantity">${ICON.minus}</button><output class="zd-qty__val" aria-live="polite">${this.qty}</output><button type="button" class="zd-stepper__btn" data-action="qty+" aria-label="Increase quantity">${ICON.plus}</button></div></div>`;
    const scope = `<p class="zd-scope">${this.state.shelf.type === 'none' ? 'Gallery rail only — fits your own shelf.' : 'Gallery rail with made-to-measure shelf.'} Wall anchors and installation are not included.</p>`;
    const inline = this.root.querySelector('[data-zd-action]');
    const barEl = this.root.querySelector('[data-zd-bar]');
    const stageEl = this.root.querySelector('[data-zd-stagebar]');
    if (this._lastMode !== p.mode) this._cartErr = '';
    this._lastMode = p.mode;
    inline.innerHTML = `<h2 class="zd-action__title">Your gallery rail</h2><p class="zd-action__summary">${summary}</p>${this.breakdownHTML()}${qty}${scope}
      <div class="zd-action__ctas">${addBtn}${p.mode === 'direct' ? '<button type="button" class="zd-btn zd-btn--secondary" data-action="buy-now"><span class="zd-btn__label">Buy it now</span><span class="zd-spinner" aria-hidden="true"></span></button>' : ''}</div>
      <p class="zd-action__ref zd-small zd-muted">Configuration ID <strong>${core.designRef(this.state)}</strong></p><div class="zd-action__error" data-cart-error role="alert" hidden></div>`;
    if (stageEl) stageEl.innerHTML = `<div class="zd-stagebar__info">${priceBlock}<p class="zd-action__summary">${summary}</p></div><div class="zd-action__ctas">${addBtn}</div>${scope}<div class="zd-action__error" data-cart-error role="alert" hidden></div>`;
    if (barEl) barEl.innerHTML = `<div class="zd-price"><span class="zd-price__label">${this.qty > 1 ? `${this.qty} sets` : 'Your rail'}</span><span class="zd-price__dims">${esc(sum.size)}</span></div><div class="zd-action__ctas">${addBtn}</div>`;
    if (this._cartErr) this.showCartError(this._cartErr);
    if (this.busy) this.root.querySelectorAll('[data-action="add-to-cart"],[data-action="buy-now"]').forEach((b) => b.classList.add('is-loading'));
  }
  renderIncluded() {
    const items = core.includedComponents(this.state, this.config, this.pricing.posts);
    document.querySelectorAll('[data-zd-included-target]').forEach((el) => {
      el.innerHTML = `<ul class="zd-list zd-list--check">${items.map((x) => `<li>${esc(x)}</li>`).join('')}</ul><p class="zd-scope">Not included: ${core.excludedFor(this.state).map((x) => esc(x.toLowerCase())).join('; ')}.</p>`;
    });
  }

  /* ============================================================ preview */
  currentModel(state = this.state) {
    const f = this.config.finishes.find((x) => x.id === state.finish);
    return core.railModel(state, this.config, f);
  }
  updatePreview() {
    const m = this.currentModel();
    const svgHost = this.root.querySelector('[data-zd-fallback-svg]');
    if (svgHost && this.viewerMode !== 'webgl') svgHost.innerHTML = core.elevationSVG(m, { stroke: m.finish.color });
    if (this.viewer && this.validation.valid) this.viewer.update(m);
  }
  async initViewer() {
    const host = this.root.querySelector('[data-zd-canvas]');
    const status = this.root.querySelector('[data-zd-viewer-status]');
    const fallback = (reason) => { this.viewerMode = '2d'; this.root.classList.add('zd--2d'); status.hidden = false; status.textContent = reason; this.updatePreview(); this.thumbFallback(); };
    const supportsGL = (() => { try { const c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; } })();
    if (!supportsGL) return fallback('3D preview is not available on this device, so we are showing a dimensioned drawing. All options still work.');
    const A = this.config.assets;
    if (!A.three || !A.viewer) return fallback('3D preview is not configured. Showing a dimensioned drawing.');
    status.hidden = false;
    status.textContent = 'Loading 3D preview…';
    const go = async () => {
      try {
        const [THREE, mod] = await Promise.all([import(A.three), import(A.viewer)]);
        this.THREE = THREE; this.mod3d = mod;
        this.viewer = mod.createViewer(host, THREE, { onError: () => { this.destroyViewer(); fallback('The 3D preview stopped responding, so we switched to a dimensioned drawing.'); } });
        this.viewerMode = 'webgl';
        this.root.classList.add('zd--3d');
        status.hidden = true;
        this.viewer.update(this.currentModel());
        if (this.dimsOn === false) this.viewer.setDims(false);
        this.queueThumbs(true);
      } catch (e) {
        console.error('[zd-rail] 3D viewer failed', e);
        fallback('The 3D preview could not load, so we are showing a dimensioned drawing. All options still work.');
      }
    };
    (window.requestIdleCallback || ((cb) => setTimeout(cb, 120)))(go, { timeout: 1500 });
  }

  /* ------------------------------------------------------------ 3D option thumbnails */
  thumbJobs() {
    const s = this.state;
    const base = (patch = {}) => {
      const t = structuredClone(s);
      t.units = 'in'; t.layout = 'front'; t.mount = 'top'; t.ends = 'flush'; t.spacing = 'standard'; t.rails = s.post === 'sphere' ? 2 : 1;
      t.dims = { L: IN(9), R: IN(4), H: IN(3.5) };
      t.shelf = { type: 'none', wood: 'oak', auto: false, L: IN(13), D: IN(4), T: 1 };
      t.brackets = { type: 'none', count: 0 };
      const deep = (o, p) => { for (const k in p) { if (p[k] && typeof p[k] === 'object' && !Array.isArray(p[k])) deep(o[k], p[k]); else o[k] = p[k]; } };
      deep(t, patch);
      return this.currentModel(t);
    };
    const jobs = [];
    core.POSTS.forEach((p) => jobs.push({ key: 'post:' + p.id, model: base({ post: p.id, rails: p.id === 'sphere' ? 2 : 1 }), view: { theta: 0.38, phi: 1.36, fov: 26, target: [2.4, 2.0, 3.3], radius: 13 } }));
    core.LAYOUTS.forEach((l) => jobs.push({ key: 'layout:' + l.id, model: base({ layout: l.id, dims: { L: IN(22), R: IN(6) }, shelf: { L: IN(24), D: IN(8), T: 1.5 } }), view: { theta: 0.3, phi: 0.78, fov: 30, target: [0, 0, 4], radius: 58 } }));
    [1, 2, 3].forEach((n) => jobs.push({ key: 'rails:' + n, model: base({ rails: n, dims: { H: IN(4) } }), view: { theta: 0.35, phi: 1.4, fov: 26, target: [0, 2.2, 3.3], radius: 27 } }));
    ['standard', 'close', 'gallery'].forEach((sp) => jobs.push({ key: 'spacing:' + sp, model: base({ spacing: sp, dims: { L: IN(34) }, shelf: { L: IN(36), D: IN(6) } }), view: { theta: 0.12, phi: 1.38, fov: 24, target: [0, 1.4, 5], radius: 62 } }));
    ['flush', 'ball', 'finial'].forEach((e) => jobs.push({ key: 'ends:' + e, model: base({ ends: e }), view: { theta: 0.55, phi: 1.32, fov: 24, target: [3.6, 2.4, 3.3], radius: 14 } }));
    core.SHELVES.forEach((sh) => jobs.push({ key: 'shelfType:' + sh.id, model: base({ shelf: { type: sh.id, L: IN(20), D: IN(8), T: 1.5 }, dims: { L: IN(18) } }), view: { theta: 0.42, phi: 1.1, fov: 28, target: [0, 0, 4], radius: 46 } }));
    core.WOODS.forEach((w) => jobs.push({ key: 'wood:' + w.id, model: base({ shelf: { type: 'wood', wood: w.id, L: IN(14), D: IN(7), T: 1.5 }, dims: { L: IN(12) } }), view: { theta: 0.5, phi: 1.05, fov: 28, target: [0, -0.5, 3.5], radius: 34 } }));
    core.BRACKETS.forEach((b) => jobs.push({ key: 'bracket:' + b.id, model: base({ shelf: { type: s.shelf.type === 'none' ? 'wood' : s.shelf.type, L: IN(10), D: IN(9), T: 1.5 }, dims: { L: IN(8) }, brackets: { type: b.id, count: b.id === 'none' ? 0 : 1 } }), view: { theta: 1.05, phi: 1.58, fov: 30, target: [0, -3.8, 4.5], radius: 30 } }));
    return jobs.map((j) => ({ ...j, model: { ...j.model, brackets: { ...j.model.brackets, count: j.key.startsWith('bracket:') ? (j.key === 'bracket:none' ? 0 : 1) : 0 } } }));
  }
  queueThumbs(now) {
    if (!this.THREE) return;
    const s = this.state;
    const sig = JSON.stringify([s.finish, s.post, s.shelf.type, s.shelf.wood]);
    if (sig === this._thumbSig) return;
    clearTimeout(this._thumbT);
    this._thumbT = setTimeout(async () => {
      if (this._thumbBusy) { this._thumbSig = null; return this.queueThumbs(); }
      this._thumbBusy = true;
      this._thumbSig = sig;
      try {
        const all = this.thumbJobs();
        const jobs = all.filter((j) => this.thumbs[j.key] !== sig);
        if (!this.viewer) throw new Error('no viewer');
        const out = await this.viewer.thumbs(jobs);
        Object.entries(out).forEach(([k, url]) => {
          this.thumbs[k] = sig;
          this.root.querySelectorAll(`[data-thumb="${CSS.escape(k)}"]`).forEach((el) => { el.innerHTML = `<img src="${url}" alt="" decoding="async">`; el.classList.add('is-3d'); });
        });
      } catch (e) { console.warn('[zd-rail] thumbnails failed', e); this.thumbFallback(); }
      this._thumbBusy = false;
    }, now ? 50 : 700);
  }
  thumbFallback() { this.root.querySelectorAll('.zd-thumb:not(.is-3d)').forEach((el) => el.classList.add('is-empty')); }
  destroyViewer() { if (this.viewer) { try { this.viewer.dispose(); } catch (e) { /* ignore */ } this.viewer = null; this.root.classList.remove('zd--3d'); } }
  destroy() { this.destroyViewer(); }
  setView(name) { this.root.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === name))); if (this.viewer) this.viewer.setView(name); }
  toggleFullscreen(force) {
    const el = this.root.querySelector('[data-zd-viewer]');
    const isFs = document.fullscreenElement === el || this.root.classList.contains('zd--fs-fallback');
    const want = force === undefined ? !isFs : force;
    if (want) { if (el.requestFullscreen) el.requestFullscreen().catch(() => this.root.classList.add('zd--fs-fallback')); else this.root.classList.add('zd--fs-fallback'); }
    else { if (document.fullscreenElement) document.exitFullscreen(); this.root.classList.remove('zd--fs-fallback'); }
    setTimeout(() => this.onFullscreenChange(), 60);
  }
  onFullscreenChange() {
    const el = this.root.querySelector('[data-zd-viewer]');
    const on = document.fullscreenElement === el || this.root.classList.contains('zd--fs-fallback');
    this.root.querySelector('[data-action="fullscreen"]')?.setAttribute('aria-pressed', String(on));
    el.classList.toggle('is-fullscreen', on);
    if (this.viewer) this.viewer.resize();
  }
  caption() { const L = core.labelsFor(this.state, this.config); return `${core.designRef(this.state)} · ${L.post} · ${L.length} · ${L.height} high · ${L.finish} · ${this.config.brand}`; }
  async previewPNG() {
    if (this.viewer) return this.viewer.snapshot({ caption: this.caption() });
    const svg = core.elevationSVG(this.currentModel(), { stroke: this.currentModel().finish.color });
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const c = document.createElement('canvas');
    c.width = 1600; c.height = Math.round(1600 * (img.height / img.width || 0.4)) + 60;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#f6f1e8'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, 1600, c.height - 60);
    ctx.fillStyle = '#2b2925'; ctx.font = '500 24px system-ui, sans-serif'; ctx.fillText(this.caption(), 24, c.height - 24);
    URL.revokeObjectURL(url);
    return c.toDataURL('image/png');
  }

  /* ============================================================ actions */
  async action(a, btn) {
    const s = this.state;
    switch (a) {
      case 'reset-view': this.setView('perspective'); break;
      case 'dims': this.dimsOn = btn.getAttribute('aria-pressed') !== 'true'; btn.setAttribute('aria-pressed', String(this.dimsOn)); if (this.viewer) this.viewer.setDims(this.dimsOn); break;
      case 'fullscreen': this.toggleFullscreen(); break;
      case 'pin': { const on = !this.root.classList.contains('zd--pinned'); this.root.classList.toggle('zd--pinned', on); btn.setAttribute('aria-pressed', String(on)); if (this.viewer) setTimeout(() => this.viewer.resize(), 50); break; }
      case 'png': try { const data = await this.previewPNG(); const l = document.createElement('a'); l.href = data; l.download = `gallery-rail-${core.designRef(s)}.png`; document.body.appendChild(l); l.click(); l.remove(); } catch (e) { this.announce('Sorry, the image could not be created.'); } break;
      case 'share': { const box = this.root.querySelector('[data-share]'); const url = this.shareURL(); box.hidden = false; box.querySelector('[data-share-url]').value = url; await this.copy(url); break; }
      case 'copy': await this.copy(this.root.querySelector('[data-share-url]').value); break;
      case 'print': await this.print(); break;
      case 'reset': this.confirmReset(); break;
      case 'dismiss-notice': this.root.querySelector('[data-zd-notice]').hidden = true; break;
      case 'first-error': this.focusFirstError(); break;
      case 'qty-': this.qty = Math.max(1, this.qty - 1); this.update(); break;
      case 'qty+': if (this.qty >= this.config.pricing.maxDirectQty) { this.showCartError(`You can order up to ${this.config.pricing.maxDirectQty} of one configuration at a time.`); break; } this.qty += 1; this.update(); break;
      case 'bc-': this.set({ brackets: { ...s.brackets, count: Math.max(2, core.bracketCount(s) - 1) } }, { announce: 'Bracket removed' }); break;
      case 'bc+': this.set({ brackets: { ...s.brackets, count: Math.min(8, core.bracketCount(s) + 1) } }, { announce: 'Bracket added' }); break;
      case 'bc-auto': this.set({ brackets: { ...s.brackets, count: 0 } }, { announce: 'Recommended number of brackets' }); break;
      case 'add-to-cart': await this.addToCart(false); break;
      case 'buy-now': await this.addToCart(true); break;
      default: break;
    }
  }
  shareURL() { const u = new URL(location.href); u.search = ''; u.hash = ''; u.searchParams.set('design', core.encodeShare(this.state)); return u.toString(); }
  async copy(text) {
    try { await navigator.clipboard.writeText(text); this.announce('Share link copied', true); }
    catch (e) { const inp = this.root.querySelector('[data-share-url]'); if (inp) { inp.focus(); inp.select(); } this.announce('Select and copy the link shown.', true); }
  }
  confirmReset() {
    const dlg = this.root.querySelector('[data-zd-reset-dialog]');
    const doReset = () => {
      safeStorage.del(STORAGE_KEY);
      this.raw = {}; this.qty = 1;
      this.state = core.defaultState(this.config);
      const n = this.root.querySelector('[data-zd-notice]'); if (n) n.hidden = true;
      if (location.search.includes('design=')) history.replaceState(null, '', location.pathname);
      this.update({ announce: 'Design reset to defaults' });
      if (this.viewer) this.viewer.reset();
    };
    if (dlg && typeof dlg.showModal === 'function') { dlg.showModal(); dlg.addEventListener('close', () => { if (dlg.returnValue === 'confirm') doReset(); }, { once: true }); }
    else if (window.confirm('Reset your design?')) doReset();
  }
  goToStep(key) {
    const el = this.root.querySelector(`#${this.ids('step-' + key)}`);
    if (!el) return;
    el.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    const f = el.querySelector('input:checked, input:not([type=hidden]), button');
    if (f) setTimeout(() => f.focus({ preventScroll: true }), 300);
  }
  focusFirstError() {
    const err = this.root.querySelector('[data-zd-steps] .zd-field__error:not([hidden])');
    if (!err) return;
    const field = err.closest('.zd-field, .zd-fieldset, .zd-step');
    const inp = field && field.querySelector('input');
    (inp || err).scrollIntoView({ block: 'center' });
    if (inp) inp.focus({ preventScroll: true });
  }
  announce(msg, assertive) {
    const el = this.root.querySelector(assertive ? '[data-zd-live-assertive]' : '[data-zd-live]');
    if (!el) return;
    clearTimeout(this._annT);
    this._annT = setTimeout(() => { el.textContent = ''; requestAnimationFrame(() => (el.textContent = msg)); }, assertive ? 0 : 400);
  }
  async print() {
    const L = core.labelsFor(this.state, this.config);
    let img = '';
    try { img = `<img src="${await this.previewPNG()}" alt="Preview of gallery rail design">`; } catch (e) { /* drawing only */ }
    let host = document.querySelector('body > .zd-print-root');
    if (!host) { host = document.createElement('div'); host.className = 'zd-print zd-print-root'; host.setAttribute('aria-hidden', 'true'); document.body.appendChild(host); }
    host.innerHTML = `<h1>${esc(this.config.brand)} — Custom Brass Gallery Rail</h1>
      <p>Design reference <strong>${core.designRef(this.state)}</strong> · ${new Date().toLocaleDateString()}</p>
      <div class="zd-print__media">${img}<div class="zd-print__svg">${core.elevationSVG(this.currentModel(), { stroke: '#8a6a32' })}</div></div>
      <table>${this.propRows().map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}<tr><th>Price</th><td>${esc(this.totalText())}</td></tr></table>
      <h2>Included</h2><ul>${core.includedComponents(this.state, this.config, this.pricing.posts).map((x) => `<li>${esc(x)}</li>`).join('')}</ul>
      <h2>Not included</h2><ul>${core.excludedFor(this.state).map((x) => `<li>${esc(x)}</li>`).join('')}</ul>
      <p class="zd-print__link">Reopen this design: ${esc(this.shareURL())}</p>`;
    document.documentElement.classList.add('zd-is-printing');
    setTimeout(() => window.print(), 50);
  }

  /* ============================================================ cart */
  propRows() {
    const L = core.labelsFor(this.state, this.config), s = this.state;
    return [['Post design', L.post], ['Layout', L.layout], ['Rail length', core.formatLengthBoth(core.railLengthMm(s))],
      ...(s.layout !== 'front' ? [['Return depth', core.formatLengthBoth(s.dims.R) + (s.layout === 'three' ? ' each side' : '')]] : []),
      ['Rail height', core.formatLengthBoth(s.dims.H)], ['Rails', `${L.rails}, ${L.rod}`], ['Post spacing', `${L.spacing} — ${this.pricing.posts ? this.pricing.posts.total : ''} posts`],
      ['Rail ends', L.ends], ['Mounting', L.mount], ['Shelf', L.shelf], ['Brackets', L.brackets], ['Finish', L.finish]];
  }
  cartProperties() {
    const p = this.pricing;
    const props = { 'Configuration ID': core.designRef(this.state) };
    this.propRows().forEach(([k, v]) => (props[k] = v));
    props['Units chosen'] = this.state.units === 'cm' ? 'Centimetres' : 'Inches';
    props._Components = core.includedComponents(this.state, this.config, p.posts).join('; ');
    props['_Price breakdown'] = p.lines.map((l) => `${l.label}: ${(l.cents / 100).toFixed(2)}`).join(' | ') + ` | Unit: ${(p.unitCents / 100).toFixed(2)} ${this.currency.shop}`;
    props['_Pricing version'] = PRICING.version;
    props['_Design link'] = this.shareURL();
    props._Configuration = JSON.stringify(core.compactState(this.state));
    return props;
  }
  showCartError(msg) { this._cartErr = msg || ''; this.root.querySelectorAll('[data-cart-error]').forEach((el) => { el.hidden = !msg; el.textContent = msg || ''; }); }
  async addToCart(buyNow) {
    if (this.busy) return;
    this.validation = core.validateState(this.state, this.config, this.raw);
    this.pricing = core.priceState(this.state, this.config, this.validation);
    if (this.pricing.mode !== 'direct') { this.update(); this.showCartError(this.pricing.mode === 'invalid' ? 'Please correct the highlighted fields first.' : 'This configuration cannot be added to the cart right now. Please contact us for a quote.'); return; }
    const variant = this.pricing.variant;
    const expected = Number(variant.price);
    this.busy = true;
    this.showCartError('');
    this.root.querySelectorAll('[data-action="add-to-cart"],[data-action="buy-now"]').forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); });
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // Only a variant id and quantity are sent; Shopify charges that variant's own price.
      const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: this.qty, properties: this.cartProperties() }] }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'We could not add this rail to your cart. Please try again.');
      const item = (data.items && data.items[0]) || data;
      if (item && typeof item.price === 'number' && item.price !== expected) { this.showCartError('The cart returned a different price for this design. Please refresh the page before checking out.'); return; }
      this.announce('Gallery rail added to your cart', true);
      if (buyNow) { location.href = `${root}checkout`; return; }
      if (this.config.cart?.afterAdd !== 'cart' && document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: data } }));
      } else location.href = `${root}cart`;
    } catch (e) { this.showCartError(e.message || 'Something went wrong. Please try again.'); }
    finally {
      this.busy = false;
      this.root.querySelectorAll('[data-action="add-to-cart"],[data-action="buy-now"]').forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); });
    }
  }
}

function init() {
  document.querySelectorAll('[data-zd-rail-builder]:not([data-zd-ready])').forEach((el) => { el.setAttribute('data-zd-ready', ''); el.__zdBuilder = new RailBuilder(el); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
document.addEventListener('shopify:section:load', init);
